// Pattern model + multi-pattern bank.
//
// A Pattern has many tracks. A track key is opaque to the pattern itself —
// the channel rack chooses keys like "kick" for drums or "synth:C4" for
// pitched lanes. This keeps patterns generic enough to host any future module.

// Unique-enough IDs avoid collisions when loading a saved project that
// contains the same sequential names this session would otherwise generate.
function makeId() {
    return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

let nameCounter = 1;

export function createPattern({ id, name, length = 16 } = {}) {
    return {
        id: id ?? makeId(),
        name: name ?? `Pattern ${nameCounter++}`,
        length,
        tracks: {}, // trackKey → { steps: bool[], note?: string, velocity?: number }
    };
}

// Step values are velocities in [0, 1]. 0 means "off". Anything > 0 is "on
// at that velocity". A parallel `lengths` array stores the duration of each
// note in 16th-step units (default 1). lengths only matter for pitched lanes
// — drum lanes ignore them.
export const DEFAULT_VELOCITY = 0.85;
export const DEFAULT_LENGTH = 1;
export const DEFAULT_PROBABILITY = 1;

export function ensureTrack(pattern, key, init = {}) {
    if (!pattern.tracks[key]) {
        pattern.tracks[key] = {
            steps: Array(pattern.length).fill(0),
            lengths: Array(pattern.length).fill(DEFAULT_LENGTH),
            probabilities: Array(pattern.length).fill(DEFAULT_PROBABILITY),
            note: init.note ?? null,
        };
    } else {
        const t = pattern.tracks[key];
        // Migrate boolean steps from older saved projects.
        if (t.steps.length && typeof t.steps[0] === 'boolean') {
            t.steps = t.steps.map(b => (b ? DEFAULT_VELOCITY : 0));
        }
        if (!t.lengths || t.lengths.length !== t.steps.length) {
            t.lengths = Array(t.steps.length).fill(DEFAULT_LENGTH);
        }
        if (!t.probabilities || t.probabilities.length !== t.steps.length) {
            t.probabilities = Array(t.steps.length).fill(DEFAULT_PROBABILITY);
        }
        if (t.steps.length !== pattern.length) {
            const next = Array(pattern.length).fill(0);
            const nextLen = Array(pattern.length).fill(DEFAULT_LENGTH);
            const nextProb = Array(pattern.length).fill(DEFAULT_PROBABILITY);
            for (let i = 0; i < Math.min(t.steps.length, pattern.length); i++) {
                next[i] = t.steps[i];
                nextLen[i] = t.lengths[i] || DEFAULT_LENGTH;
                nextProb[i] = t.probabilities[i] ?? DEFAULT_PROBABILITY;
            }
            t.steps = next;
            t.lengths = nextLen;
            t.probabilities = nextProb;
        }
    }
    return pattern.tracks[key];
}

export function setPatternLength(pattern, length) {
    pattern.length = length;
    for (const key of Object.keys(pattern.tracks)) {
        ensureTrack(pattern, key);
    }
}

export function createPatternBank(bus) {
    const patterns = new Map();
    const order = [];
    let activeId = null;

    function add(pattern) {
        patterns.set(pattern.id, pattern);
        order.push(pattern.id);
        if (!activeId) activeId = pattern.id;
        bus.emit('pattern:added', pattern);
        bus.emit('pattern:listChanged', list());
        return pattern;
    }

    function remove(id) {
        if (!patterns.has(id)) return;
        if (order.length <= 1) return; // always keep at least one
        patterns.delete(id);
        const i = order.indexOf(id);
        if (i >= 0) order.splice(i, 1);
        if (activeId === id) activeId = order[0];
        bus.emit('pattern:removed', id);
        bus.emit('pattern:listChanged', list());
        bus.emit('pattern:activeChanged', getActive());
    }

    function setActive(id) {
        if (!patterns.has(id) || activeId === id) return;
        activeId = id;
        bus.emit('pattern:activeChanged', getActive());
    }

    function rename(id, name) {
        const p = patterns.get(id);
        if (!p) return;
        p.name = name;
        bus.emit('pattern:listChanged', list());
    }

    function duplicate(id) {
        const src = patterns.get(id);
        if (!src) return null;
        const copy = createPattern({ name: `${src.name} copy`, length: src.length });
        // Deep-clone every track field — including the parallel `lengths`
        // and `probabilities` arrays that earlier versions of this code
        // accidentally dropped.
        for (const [k, t] of Object.entries(src.tracks)) {
            copy.tracks[k] = {
                steps: (t.steps || []).slice(),
                lengths: (t.lengths || []).slice(),
                probabilities: (t.probabilities || []).slice(),
                note: t.note ?? null,
            };
        }
        return add(copy);
    }

    function getActive() { return patterns.get(activeId); }
    function list() { return order.map(id => patterns.get(id)); }

    // Atomic full reset — used by project load. Bypasses the "keep at least
    // one" rule that the interactive Delete button relies on.
    function replaceAll(newPatterns, newActiveId) {
        patterns.clear();
        order.length = 0;
        for (const p of newPatterns) {
            patterns.set(p.id, p);
            order.push(p.id);
        }
        activeId = newActiveId && patterns.has(newActiveId) ? newActiveId : order[0] || null;
        bus.emit('pattern:listChanged', list());
        bus.emit('pattern:activeChanged', getActive());
    }

    return {
        add, remove, setActive, rename, duplicate, replaceAll,
        getActive, list,
        getActiveId: () => activeId,
    };
}
